import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, Printer, Save, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, PageHead, Panel, inputCls } from "@/components/kit";
import { AutoPlace, BanTools, ScaleTools } from "@/components/PlanTools";
import { TableEditor } from "@/components/TableEditor";
import { api, errMsg } from "@/lib/api";

const SEDI = ["AFRODITE BOUTIQUE HOTEL", "TENUTA LEDO", "CATERING PRESSO SEDE ESTERNA"];

function NewPlan({ onDone }) {
  const [f, setF] = useState({ nome: "", sede: SEDI[0], larghezza_m: 20 });
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    if (!f.nome.trim()) return toast.error("Dai prima un nome alla sala");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      onDone((await api.post("/floorplans", fd)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Panel title="Nuova sala" testId="new-plan">
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Nome sala"><input className={inputCls} value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} placeholder="Sala Mare, Giardino…" data-testid="plan-name" /></Field>
        <Field label="Struttura"><select className={inputCls} value={f.sede} onChange={(e) => setF({ ...f, sede: e.target.value })} data-testid="plan-sede">{SEDI.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Larghezza reale (m) • la scala la legge poi l'IA"><input type="number" className={inputCls} value={f.larghezza_m} onChange={(e) => setF({ ...f, larghezza_m: e.target.value })} data-testid="plan-width" /></Field>
      </div>
      <label className={`inline-flex items-center gap-2 rounded-full bg-[#0B6E8F] text-white px-5 py-2.5 text-sm cursor-pointer ${busy ? "opacity-60" : ""}`} data-testid="plan-upload"><Upload size={15} /> {busy ? "Carico…" : "Carica la planimetria (foto o PDF)"}<input type="file" accept="image/*,application/pdf" className="hidden" disabled={busy} onChange={(e) => e.target.files[0] && up(e.target.files[0])} /></label>
    </Panel>
  );
}

function Editor({ plan, onSaved, onDelete }) {
  const [tables, setTables] = useState(plan.tables || []);
  const [dirty, setDirty] = useState(false);
  const [picks, setPicks] = useState(null);
  const [areaMode, setAreaMode] = useState(null);
  const [draft, setDraft] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (areaMode || picks?.length === 0 || draft?.length === 0) document.querySelector("[data-testid=plan-editor-map]")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [areaMode, picks, draft]);
  const tapArea = async ([x, y]) => {
    const add = areaMode === "add";
    setAreaMode(null);
    try {
      onSaved((await api.post(`/floorplans/${plan.id}/area`, { x, y, add })).data);
      toast.success("Area aggiornata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    setTables(plan.tables || []);
    setDirty(false);
    setPicks(null);
  }, [plan.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (fn) => { setTables(fn); setDirty(true); };
  const save = async () => {
    try {
      onSaved((await api.put(`/floorplans/${plan.id}`, { tables })).data);
      toast.success("Sala salvata");
      setDirty(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <TableEditor plan={plan} tables={tables} onChange={change}
      mapProps={{ showAi: true, picks, draft, onMapClick: picks && picks.length < 2 ? (p) => setPicks([...picks, p]) : areaMode ? tapArea : draft ? (p) => setDraft([...draft, p]) : undefined }}
      side={<>
        <p className="text-sm text-[#47606B] rounded-lg bg-[#EAF5F7] p-3" data-testid="master-hint">Qui disegni la <b>disposizione massima</b> della sala. Per ogni evento, nella scheda del contratto, l'app sceglie solo i tavoli che servono.</p>
        <ScaleTools plan={plan} onPlan={onSaved} picks={picks} setPicks={setPicks} areaMode={areaMode} setAreaMode={setAreaMode} />
        <BanTools plan={plan} onPlan={onSaved} draft={draft} setDraft={setDraft} />
        <AutoPlace plan={{ ...plan, tables }} onTables={(ts) => change(() => ts)} />
      </>}
      footer={
        <div className="flex flex-wrap gap-2 pt-2 border-t border-[#EAF5F7]">
          <Button className="rounded-full" disabled={!dirty} onClick={save} data-testid="plan-save"><Save size={15} className="mr-1" /> Salva sala</Button>
          <Button variant="outline" className="rounded-full" onClick={() => (!dirty || window.confirm("Ci sono modifiche non salvate. Andare alla stampa senza salvarle?")) && nav(`/sale/${plan.id}/stampa`)} data-testid="plan-print"><Printer size={15} className="mr-1" /> Stampa</Button>
          <Button variant="ghost" className="rounded-full text-[#991B1B]" onClick={onDelete} data-testid="plan-delete"><Trash2 size={14} /></Button>
        </div>
      } />
  );
}

export default function FloorPlans() {
  const [plans, setPlans] = useState(null);
  const [cur, setCur] = useState(null);
  const load = () => api.get("/floorplans").then((r) => setPlans(r.data));
  useEffect(() => {
    load();
  }, []);
  const plan = plans?.find((p) => p.id === cur);
  const del = async () => {
    if (!window.confirm("Eliminare questa sala?")) return;
    await api.delete(`/floorplans/${cur}`);
    setCur(null);
    load();
  };
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Planimetrie e disposizione massima dei tavoli" title="Sale" />
      <div className="flex flex-wrap gap-2">
        {plans?.map((p) => <button key={p.id} onClick={() => setCur(p.id)} className={`px-4 py-2 rounded-full text-sm transition-colors ${cur === p.id ? "bg-[#0D2B3A] text-white" : "bg-white border border-[#D4E6EC] hover:border-[#0B6E8F]"}`} data-testid={`plan-tab-${p.id}`}>{p.nome} <span className="opacity-60 text-xs">• {p.sede.split(" ")[0]} • {p.tables.length} tavoli</span></button>)}
        <button onClick={() => setCur(null)} className={`px-4 py-2 rounded-full text-sm ${!cur ? "bg-[#34B3B0] text-white" : "border border-dashed border-[#34B3B0] text-[#0B6E8F]"}`} data-testid="plan-new-tab"><Plus size={13} className="inline -mt-0.5" /> Nuova sala</button>
      </div>
      {!cur && <NewPlan onDone={(p) => { load().then(() => setCur(p.id)); }} />}
      {plan && <Editor plan={plan} onSaved={(p) => setPlans(plans.map((x) => (x.id === p.id ? p : x)))} onDelete={del} />}
    </div>
  );
}
